import { Link } from "react-router-dom";
import { ArrowRight, Plane, Luggage, Clock, MapPin } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import AirZimbabweLogo from "@/components/AirZimbabweLogo";
import Seo from "@/components/Seo";
import { AIR_ZIMBABWE, DESTINATIONS } from "@/lib/data";

const HERO = "https://upload.wikimedia.org/wikipedia/commons/thumb/a/aa/Air_Zimbabwe_Boeing_767-200ER_Pichugin-1.jpg/1280px-Air_Zimbabwe_Boeing_767-200ER_Pichugin-1.jpg";
const FLEET = "https://upload.wikimedia.org/wikipedia/commons/thumb/a/aa/Air_Zimbabwe_Boeing_767-200ER_Pichugin-1.jpg/1280px-Air_Zimbabwe_Boeing_767-200ER_Pichugin-1.jpg";

export default function AirZimbabwePage() {
  return (
    <div data-testid="air-zimbabwe-page">
      <Seo
        title="Air Zimbabwe UK — Flights, Route Network, Fleet & Schedule | Simba Travel"
        description="Book Air Zimbabwe flights from the UK. Full route network, London Gatwick–Harare schedule, fleet information, baggage allowance and check-in details — supported by the appointed UK GSA."
      />
      <section className="relative h-[70vh] min-h-[480px] -mt-24 flex items-end overflow-hidden">
        <img src={HERO} alt="Air Zimbabwe" className="absolute inset-0 w-full h-full object-cover object-left" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight to-midnight/30" />
        <div className="relative container-edge pb-16 text-linen">
          <div className="overline text-ochre mb-4">Airline profile · UM</div>
          <AirZimbabweLogo className="h-16 md:h-20 w-auto mb-6" variant="light" />
          <h1 className="editorial-h1 max-w-3xl">Air Zimbabwe.</h1>
          <p className="mt-4 text-linen/80 max-w-xl">The flag carrier of Zimbabwe — represented in the United Kingdom by Simba Travel.</p>
        </div>
      </section>

      {/* Overview */}
      <section className="section-pad bg-linen">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <Reveal className="md:col-span-5">
            <div className="overline text-terracotta mb-4">Airline overview</div>
            <h2 className="editorial-h2">Wings over the Zambezi since {AIR_ZIMBABWE.founded}.</h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7 md:pt-4">
            <p className="text-lg leading-relaxed text-ink/80 font-light">
              Founded in {AIR_ZIMBABWE.founded}, Air Zimbabwe operates scheduled passenger services from its primary hub at {AIR_ZIMBABWE.hub}. The airline's role is to connect Zimbabwean travellers to the world and to bring international visitors into the heart of Southern Africa.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              <div className="border border-ink/10 p-5">
                <div className="overline">IATA / ICAO</div>
                <div className="font-serif text-2xl mt-2">UM / AZW</div>
              </div>
              <div className="border border-ink/10 p-5">
                <div className="overline">Hub</div>
                <div className="text-sm mt-2">Harare (HRE)</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Route Network */}
      <section className="section-pad bg-sand">
        <div className="container-edge">
          <SectionHeading
            overline="Route network"
            title="Where Air Zimbabwe flies."
            lede="A network anchored on Harare, with the London — Harare service supported by Simba Travel in the United Kingdom and onward regional services across Southern and East Africa."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {AIR_ZIMBABWE.routes.map((r) => (
              <div key={r} className="bg-linen p-6 flex items-center gap-3">
                <Plane className="w-4 h-4 stroke-1 text-terracotta -rotate-45" />
                <span className="text-sm">{r}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            {DESTINATIONS.slice(0, 8).map((d) => (
              <Link
                key={d.slug}
                to={`/destinations/${d.slug}`}
                className="text-xs tracking-widest uppercase border border-ink/20 px-4 py-2 hover:bg-midnight hover:text-linen hover:border-midnight transition-colors"
              >
                {d.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Flight schedule */}
      <section className="section-pad bg-linen" data-testid="az-schedule-section">
        <div className="container-edge">
          <SectionHeading
            overline="Flight schedule"
            title={AIR_ZIMBABWE.schedule.title + "."}
            lede={AIR_ZIMBABWE.schedule.subtitle + " · " + AIR_ZIMBABWE.schedule.frequency + " nonstop."}
          />

          <div className="mt-10 border border-ink/10 overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-midnight text-linen">
                <tr>
                  <th className="text-left p-4 font-medium text-xs tracking-widest uppercase">Rotation</th>
                  <th className="text-left p-4 font-medium text-xs tracking-widest uppercase">Effective dates</th>
                  <th className="text-left p-4 font-medium text-xs tracking-widest uppercase">Outbound (LGW → HRE)</th>
                  <th className="text-left p-4 font-medium text-xs tracking-widest uppercase">Return (HRE → LGW)</th>
                  <th className="text-left p-4 font-medium text-xs tracking-widest uppercase">Flight No.</th>
                </tr>
              </thead>
              <tbody>
                {AIR_ZIMBABWE.schedule.rows.map((r, i) => (
                  <tr key={`${r.flightNo}-${r.effective}`} className={`border-t border-ink/10 ${i % 2 === 0 ? "bg-linen" : "bg-sand"}`} data-testid={`schedule-row-${i}`}>
                    <td className="p-4 font-serif text-base whitespace-nowrap">{r.rotation}</td>
                    <td className="p-4 whitespace-nowrap">{r.effective}</td>
                    <td className="p-4 tabular-nums">{r.outbound}</td>
                    <td className="p-4 tabular-nums">{r.inbound}</td>
                    <td className="p-4 font-mono text-xs">{r.flightNo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-muted2 mt-4 italic">{AIR_ZIMBABWE.schedule.footnote}</p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {AIR_ZIMBABWE.schedule.bullets.map((b, i) => (
              <div key={b} className="border-l-2 border-terracotta pl-5 py-2">
                <div className="overline text-terracotta mb-2">Note {i + 1}</div>
                <p className="text-sm text-ink/80 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet */}
      <section className="section-pad bg-linen">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-5">
            <img src={FLEET} alt="Aircraft" className="w-full aspect-[4/3] object-contain bg-midnight" />
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7">
            <div className="overline text-terracotta mb-4">Fleet information</div>
            <h2 className="editorial-h2 mb-8">A mixed wide-body and regional fleet.</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-ink/10">
                <thead className="bg-sand">
                  <tr>
                    <th className="text-left p-4 font-medium text-xs tracking-widest uppercase text-muted2">Aircraft</th>
                    <th className="text-left p-4 font-medium text-xs tracking-widest uppercase text-muted2">In service</th>
                    <th className="text-left p-4 font-medium text-xs tracking-widest uppercase text-muted2">Seats</th>
                    <th className="text-left p-4 font-medium text-xs tracking-widest uppercase text-muted2">Range</th>
                  </tr>
                </thead>
                <tbody>
                  {AIR_ZIMBABWE.fleet.map((f) => (
                    <tr key={f.type} className="border-t border-ink/10">
                      <td className="p-4 font-serif text-lg">{f.type}</td>
                      <td className="p-4">{f.count}</td>
                      <td className="p-4">{f.seats}</td>
                      <td className="p-4">{f.range}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Baggage + Check-in */}
      <section className="section-pad bg-midnight text-linen">
        <div className="container-edge grid grid-cols-1 md:grid-cols-2 gap-px bg-linen/10">
          <div className="bg-midnight p-10 md:p-14">
            <Luggage className="w-7 h-7 stroke-1 text-ochre" />
            <h3 className="editorial-h3 text-linen mt-6">Baggage allowance</h3>
            <div className="mt-6 space-y-5">
              <div>
                <div className="overline text-ochre">Economy class</div>
                <div className="text-sm text-linen/80 mt-2">{AIR_ZIMBABWE.baggage.economy}</div>
              </div>
              <div>
                <div className="overline text-ochre">Business class</div>
                <div className="text-sm text-linen/80 mt-2">{AIR_ZIMBABWE.baggage.business}</div>
              </div>
              <p className="text-xs text-linen/60 leading-relaxed">{AIR_ZIMBABWE.baggage.notes}</p>
            </div>
          </div>
          <div className="bg-midnight p-10 md:p-14">
            <Clock className="w-7 h-7 stroke-1 text-ochre" />
            <h3 className="editorial-h3 text-linen mt-6">Check-in information</h3>
            <div className="mt-6 space-y-5">
              <div>
                <div className="overline text-ochre">Online check-in</div>
                <div className="text-sm text-linen/80 mt-2">{AIR_ZIMBABWE.checkIn.online}</div>
              </div>
              <div>
                <div className="overline text-ochre">Airport counter</div>
                <div className="text-sm text-linen/80 mt-2">{AIR_ZIMBABWE.checkIn.counter}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UK Sales Enquiry */}
      <section className="section-pad bg-linen">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline text-terracotta mb-4">UK sales support</div>
            <h2 className="editorial-h2">Book Air Zimbabwe from the UK.</h2>
            <p className="mt-6 text-muted2 leading-relaxed">
              Our agency sales and ticketing teams handle fare quotes, bookings and special service requests for Air Zimbabwe across the UK and Ireland.
            </p>
            <div className="mt-8 space-y-2 text-sm">
              <div className="flex items-center gap-3"><MapPin className="w-4 h-4 stroke-1 text-terracotta" /> Hub: Harare (HRE)</div>
              <div className="flex items-center gap-3"><Plane className="w-4 h-4 stroke-1 text-terracotta" /> Code: UM</div>
            </div>
          </div>
          <div className="md:col-span-7">
            <EnquiryForm
              defaultType="general"
              title="Air Zimbabwe booking enquiry"
              subtitle="Send us your itinerary and we'll quote within one business day."
              hiddenContext={{ subject: "Air Zimbabwe booking enquiry" }}
              testIdPrefix="airzim-enquiry"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
